import React, { Component } from 'react';
import {
  connect
} from 'react-redux';
import {
  bindActionCreators
} from 'redux';
import styled from 'styled-components';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';

import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { setPageTitle } from 'common/hooks/usePageTitle';
import { getMachineConditionLabels } from 'common/constants';

import MoreOptions from 'common/components/organisms/MoreOptions';
import FilterCountContainer from 'common/components/atoms/FilterCountContainer';
import { CollapsibleWidgetsContainer } from 'common/components/molecules';
import {
  isPetasenseAdmin, isPetasenseViewer, isAnalyst, canAccessWatchlist
} from 'common/rbac/util';
import SearchFilters from 'common/components/atoms/SearchFilters';
import { generateQueryString, isJsonString } from 'common/utils';
import { FlexContainer } from 'common/components/atoms';
import {
  getMachinesAnalyticsMetadata,
  machineAnalyticsFilters,
  filtersIncludedInCharts,
  getAlarmMetricsMetadata
} from 'home/MachinesAnalytics/machinesAnalytics.constants';
import { getMachinesAnalyticsData } from 'home/MachinesAnalytics/machinesAnalytics.actions';
import Widget from 'home/Widget';
import Modal from 'common/components/organisms/Modal';
import { history } from 'common/helpers/history';
import { widgetActions } from 'home/Widget/actions/widget.actions';
import {
  mapComponentToResource,
  operations
} from 'common/rbac/constants';
import RBAC from 'common/rbac/RBAC';
import {
  getAssetListFilterOptionLabel,
  getAssetListFilterGroupLabel
} from '../utils/assetListFilterLabels';
import {
  machinesActions
} from '../machines.actions';
import List from '../../../common/components/molecules/List';
import MachineItem from './MachineItem';
import AssetsScopeTabs from './AssetsScopeTabs';
import AssetsTable from './AssetsTable';
import AttentionQueue from './AttentionQueue';
import AttentionDetailPane from './AttentionDetailPane';
import CriteriaStrip from './CriteriaStrip';
import AppliedFilterBar from './AppliedFilterBar';
import InsightsDrawer, { INSIGHT_CARDS, TREND_WINDOWS } from './InsightsDrawer';
import {
  Search,
  ColumnIcon,
  Plus
} from '../../../common/images/FaIcons';
import MachineBuilderModal from '../MachineBuilder/MachineBuilderModal';
import * as hierarchyActions from '../../AssetHierarchy/actions/hierarchy.actions';
import AccountMenuItems from '../../../common/components/organisms/AccountMenuItems';
import * as machineBuilderActions from '../MachineBuilder/actions/machineBuilder.actions';
import { getSearchParamFilters } from '../utils';
import EditDropdown from '../../Sensors/components/EditDropdown';
import MachineCsvToolbar from './MachineCsv/MachineCsvToolbar';
import {
  ToolbarSearchBox, GhostBtn, PrimaryLinkBtn
} from '../../../common/components/molecules/ListToolbar';

const MachinesContainer = styled.div`
  background-color: ${props => props.bg || props.theme.colors.white};
  margin: ${props => props.margin ? props.margin : '0px'};
  border: ${props => props.border ? props.border : '0px'};
  border-radius: ${props => props.borderRadius ? props.borderRadius : '0px'};
  height: 100%;
`;

const ListContainer = styled(FlexContainer).attrs({ direction: 'column' })`
  position: relative;
  overflow: auto;
  width: ${props => props.width};
  min-width: 45rem;
  height: 100%;
  ${props => props.border && `border: ${props.border}`}
  ${props => props.borderRadius && `border-radius: ${props.borderRadius}`}
`;

// Filter-option row — matches the design's `.fpop-opt`.
const FilterItem = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 10px;
  text-align: left;
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-family: "Petasense Open Sans";
  font-size: 12.5px;
  color: ${props => (props.selected ? '#383838' : '#6b6e66')};

  &:hover:not(.group-name) {
    background: #eff0ee;
    color: #383838;
  }
  &.group-name {
    cursor: default;
    text-align: center;
    padding: 6px 0;
    margin-top: 4px;
    color: ${props => props.theme.colors.black};
    background-color: ${props => props.theme.colors.greyL};
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

// Label inside a filter-option row (kept separate so it doesn't size the checkbox).
const OptLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

// Design checkbox (`.cb` / `.cb.on`) used in filter-option rows.
const OptCheckbox = styled.span`
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  border: 1.5px solid #999b95;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => props.checked && `
    background: ${props.theme.primaryColor};
    border-color: ${props.theme.primaryColor};
  `}

  &::after {
    content: '';
    display: ${props => (props.checked ? 'block' : 'none')};
    width: 7px;
    height: 4px;
    border-left: 2px solid #fff;
    border-bottom: 2px solid #fff;
    transform: rotate(-45deg) translate(0, -1px);
  }
`;

// Search box inside a filter popover column (`.fpop-search`).
const OptSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e5e8e1;
  border-radius: 6px;
  padding: 5px 9px;
  margin: 4px 4px 8px;
  color: #999b95;

  svg { width: 11px; height: 11px; flex: 0 0 auto; }
`;

const OptSearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 12px;
  width: 100%;
  color: #383838;

  &::placeholder { color: #999b95; }
`;

// Manual filter keys: the "+ Filter" popover fields + quick-preset comparison.
const FILTER_CHIP_KEYS = ['area', 'condition', 'criticality', 'state', 'orientation', 'component_subtype'];

// Single source of truth for removable chips: every filter key that can be set
// from any source (+ Filter popover, insight rows, detailed-chart clicks) and
// should surface as a removable chip. Insight metric keys + attention_criteria
// are intentionally excluded here (blue insight chip / criteria strip).
const CHIP_FILTER_REGISTRY = [
  { key: 'area', titleKey: 'assets.filters.areas', def: 'Area' },
  { key: 'condition', titleKey: 'assets.filters.condition', def: 'Condition' },
  { key: 'criticality', titleKey: 'assets.filters.criticality', def: 'Criticality' },
  { key: 'state', titleKey: 'assets.filters.state', def: 'State' },
  { key: 'orientation', titleKey: 'assets.filters.orientation', def: 'Orientation' },
  { key: 'component_subtype', titleKey: 'assets.filters.componentTypes', def: 'Component type' },
  { key: 'location', titleKey: 'assets.filters.location', def: 'Location' },
  { key: 'transmission', titleKey: 'assets.filters.transmission', def: 'Transmission' },
  { key: 'vfd', titleKey: 'assets.filters.vfd', def: 'VFD' },
  { key: 'health_score', titleKey: 'assets.filters.healthScore', def: 'Health score' },
  { key: 'asset_setup', titleKey: 'assets.filters.assetSetup', def: 'Asset setup' },
  { key: 'events', titleKey: 'assets.filters.openEvents', def: 'Open events' },
  { key: 'alarm_metrics', titleKey: 'assets.filters.alarmMetrics', def: 'Alarm metrics' },
  { key: 'task_type', titleKey: 'assets.filters.taskType', def: 'Task type' }
];

// Quick-filter presets — combinations of stable string filters.
const ASSET_QUICK_PRESETS = [
  {
    id: 'high_criticality',
    labelKey: 'assets.filterBar.presets.highCriticality',
    def: 'High criticality',
    dot: '#d92b2b',
    patch: { criticality: ['Very High', 'High'] }
  },
  {
    id: 'critical_running',
    labelKey: 'assets.filterBar.presets.criticalRunning',
    def: 'Critical & running',
    dot: '#e08a1e',
    patch: { criticality: ['Very High', 'High'], state: ['ON'] }
  },
  {
    id: 'stopped',
    labelKey: 'assets.filterBar.presets.stopped',
    def: 'Stopped',
    dot: '#999b95',
    patch: { state: ['OFF'] }
  }
];

// Sort options for the filter bar. value is `field|order` ('default' = backend default).
const ASSET_SORT_OPTIONS = [
  { value: 'default', labelKey: 'assets.filterBar.sortOpts.default', def: 'Default' },
  { value: 'name|asc', labelKey: 'assets.filterBar.sortOpts.nameAsc', def: 'Name A–Z' },
  { value: 'health_score|asc', labelKey: 'assets.filterBar.sortOpts.healthScore', def: 'Health score' },
  { value: 'last_measurement_time|desc', labelKey: 'assets.filterBar.sortOpts.lastMeasurement', def: 'Last measurement' },
  { value: 'condition|desc', labelKey: 'assets.filterBar.sortOpts.condition', def: 'Condition' }
];

const ATTENTION_SORT_OPTIONS = [
  { value: 'default', labelKey: 'assets.filterBar.sortOpts.default', def: 'Default' },
  { value: 'urgency_score|desc', labelKey: 'assets.filterBar.sortOpts.urgencyScore', def: 'Urgency score' },
  { value: 'name|asc', labelKey: 'assets.filterBar.sortOpts.nameAsc', def: 'Name A–Z' },
  { value: 'setup_completion_percent|desc', labelKey: 'assets.filterBar.sortOpts.setupCompletion', def: 'Setup %' },
  { value: 'last_measurement_time|desc', labelKey: 'assets.filterBar.sortOpts.lastMeasurement', def: 'Last measurement' }
];

// Grouping options for the Needs Attention queue.
const ATTENTION_GROUP_OPTIONS = [
  { value: 'urgency', labelKey: 'assets.filterBar.groupOpts.urgency', def: 'Urgency' },
  { value: 'issue', labelKey: 'assets.filterBar.groupOpts.issue', def: 'Issue type' },
  { value: 'none', labelKey: 'assets.filterBar.groupOpts.none', def: 'None' }
];

// Metric-based insight filters (show a blue insight chip). Criteria-based
// insight rows instead drive the criteria strip via attention_criteria.
const METRIC_INSIGHTS = {
  unreviewed: {
    key: 'has_unreviewed_alarms', value: true, labelKey: 'assets.insights.alarmsNotReviewed', def: 'Alarms not reviewed'
  },
  nosensors: {
    key: 'has_sensors', value: false, labelKey: 'assets.insights.noSensors', def: 'Assets without sensors'
  },
  poorwifi: {
    key: 'has_poor_wifi', value: true, labelKey: 'assets.insights.poorWifi', def: 'Assets with poor WiFi'
  }
};
const INSIGHT_METRIC_KEYS = ['has_sensors', 'has_poor_wifi', 'has_unreviewed_alarms'];
const STRIPPED_ATTENTION_FILTERS = ['missing_asset_info', 'tasks'];
// Reverse map (searchParams key -> insight identity) so the blue insight chip
// and the drawer's active highlight are derived from searchParams, not state.
const INSIGHT_BY_KEY = {
  has_unreviewed_alarms: { filterId: 'unreviewed', labelKey: 'assets.insights.alarmsNotReviewed', def: 'Alarms not reviewed' },
  has_sensors: { filterId: 'nosensors', labelKey: 'assets.insights.noSensors', def: 'Assets without sensors' },
  has_poor_wifi: { filterId: 'poorwifi', labelKey: 'assets.insights.poorWifi', def: 'Assets with poor WiFi' }
};
const INSIGHTS_LS_KEY = 'assetsInsightsLayout';

const isFullTriageUser = user => (
  isAnalyst(user) || isPetasenseAdmin(user) || isPetasenseViewer(user)
);

class MachinesPage extends Component {
  attentionScrollRef = React.createRef();

  constructor(props) {
    super(props);
    this.stateOptions = [
      'ON',
      'OFF',
      'NA'
    ];

    this.orientationOptions = ['horizontal', 'vertical'];
    this.componentTypes = [
      'ac_induction_motor',
      'ac_synchronous_motor',
      'dc_motor',
      'steam_turbine',
      'agitator',
      'axial_compressor',
      'axial_fan',
      'axial_pump',
      'ball_mill',
      'centrifugal_compressor',
      'centrifugal_fan',
      'centrifugal_pump',
      'conveyor',
      'gear_pump',
      'reciprocating_compressor',
      'sag_mill',
      'screw_compressor',
      'screw_pump'
    ];
    this.criticalityOptions = [
      'Very High',
      'High',
      'Medium',
      'Low',
      'Very Low'
    ];
    this.taskTypeOptions = ['asset_maintenance', 'instrumentation'];
    // Column catalog (column picker). Condition is a dedicated column in the
    // redesign; setup % and condition-since stay available but off by default.
    this.allAssetColumns = ['name', 'condition', 'area_name', 'current_state', 'health_score', 'last_measurement_time', 'setup_completion_percent', 'condition_since'];
    const defaultVisibleColumns = ['name', 'condition', 'area_name', 'current_state', 'health_score', 'last_measurement_time'];
    this.initialSearchParams = this.getInitialSearchParams();

    // Initialize selected columns from localStorage or default
    const savedColumns = this.getAssetListLocalStorage('columns');
    const ahsEnabled = this.props.ahsEnabled !== false;
    let defaultColumns = [...defaultVisibleColumns];

    if (!savedColumns) {
      // Fresh user: design's default column set
      if (!ahsEnabled) {
        defaultColumns = defaultColumns.filter(col => col !== 'health_score');
      }
    } else {
      // Existing user: keep their columns, but inject the new Condition column
      const migrated = [...savedColumns];
      if (!migrated.includes('condition')) {
        const nameIdx = migrated.indexOf('name');
        migrated.splice(nameIdx >= 0 ? nameIdx + 1 : 0, 0, 'condition');
      }
      defaultColumns = migrated.filter(col => ahsEnabled || col !== 'health_score');
    }

    const insightsLayout = this.loadInsightsLayout();
    // Stripped (non-analyst) roles have no urgency score, so default the queue
    // grouping to issue-type rather than the score-based urgency buckets.
    const fullTriageView = isFullTriageUser(this.props.currentUser);

    this.state = {
      searchParams: {
        ...this.initialSearchParams
      },
      areaSearchKey: '',
      areaOptions: [],
      areaDisplay: [],
      locationTypes: [],
      transmissionOptions: [],
      reasonsForAttention: [],
      filtersOpen: {
        state: false,
        condtion: false,
        criticality: false,
        area: false,
        orientation: false,
        component_subtype: false,
        location: false,
        transmission: false
      },
      customFilterOptions: {},
      selected: _.cloneDeep(this.props.selectedOptions) || [],
      selectedColumns: defaultColumns,
      selectedDetailMachineId: null,
      attentionGroupBy: fullTriageView ? 'urgency' : 'issue',
      allAssetsCount: null,
      attentionTabCount: null,
      fieldOptionSearch: {},
      insightsOpen: false,
      insightsOrder: insightsLayout.order,
      insightsHidden: insightsLayout.hidden,
      insightTrendWindowId: '3m',
      insightDetail: null,
      pickedIds: []
    };
  }

  // Insights layout (card order + hidden set) persisted per user in localStorage.
  loadInsightsLayout = () => {
    const allIds = INSIGHT_CARDS.map(c => c.id);
    let saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(INSIGHTS_LS_KEY));
    } catch (e) {
      saved = null;
    }
    const savedOrder = saved && Array.isArray(saved.order) ? saved.order : [];
    // keep known ids in saved order, then append any new card ids
    const order = savedOrder.filter(id => allIds.includes(id))
      .concat(allIds.filter(id => !savedOrder.includes(id)));
    const hidden = saved && Array.isArray(saved.hidden)
      ? saved.hidden.filter(id => allIds.includes(id)) : [];
    return { order, hidden };
  }

  saveInsightsLayout = (order, hidden) => {
    try {
      window.localStorage.setItem(INSIGHTS_LS_KEY, JSON.stringify({ order, hidden }));
    } catch (e) {
      // ignore persistence errors
    }
  }

  getMachinesAnalyticsMetadata = () => getMachinesAnalyticsMetadata(this.props.t);

  getAlarmMetricsMetadata = () => getAlarmMetricsMetadata(this.props.t);

  getAssetListLocalStorage = (key) => {
    try {
      const assetListSettings = JSON.parse(localStorage.getItem('asset_list_settings') || '{}');
      return assetListSettings[key];
    } catch (e) {
      return null;
    }
  }

  updateAssetListLocalStorage = (key, value) => {
    try {
      let assetListSettings = JSON.parse(localStorage.getItem('asset_list_settings') || '{}');
      assetListSettings = {
        ...assetListSettings,
        [key]: value
      };
      localStorage.setItem('asset_list_settings', JSON.stringify(assetListSettings));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  componentDidMount() {
    const title = this.props.title;
    const { t } = this.props;
    setPageTitle({ partnerTitle: title, pageTitle: t('assets.tabTitle') });

    const {
      setMachinePaginater,
      initMachinePaginater,
      setMachineSearchParams,
      getAreas
    } = this.props.machinesActions;

    this.isAssetsPage = this.props.location.pathname === '/assets';

    if (this.props.location.state && this.props.location.state.filter) {
      this.selectFilterFromCharts(this.props.location.state.filter);
      setMachinePaginater(1);
    } else if (!_.isEmpty(this.props.preFilters)) {
      this.setState(prevState => ({
        searchParams: {
          ...prevState.searchParams,
          ...this.props.preFilters
        }
      }));
      setMachineSearchParams(this.props.preFilters);
      this.props.toggleModelResource(this.state.selected);
    } else if (!_.isEmpty(this.props.location.search) && this.isAssetsPage) {
      this.selectFiltersFromUrl(true);
    } else initMachinePaginater();
    getAreas().then((res) => {
      this.setState({
        areaOptions: res.result,
        areaDisplay: res.result
      });
    });

    const { getMachineBuilderMetadata } = this.props.machineBuilderActions;
    getMachineBuilderMetadata().then((res) => {
      this.setState({
        locationTypes: res.data.available_location_types,
        transmissionOptions: res.data.components.transmission.map(x => x.value),
        reasonsForAttention: res.data.reasons_for_attention
      });
    });

    this.props.widgetActions.getWidgetsMetadata();

    // Fetch the attention summary up-front so the Needs Attention tab badge
    // shows a count even while the All Assets scope is active.
    if (this.isAssetsPage) this.fetchAttentionSummary();
  }

  componentDidUpdate(prevProps) {
    if (this.isAssetsPage && !_.isEqual(this.props.location.search, prevProps.location.search)) {
      this.selectFiltersFromUrl();
    }

    // Prune the selection to what is actually in the list whenever the list
    // changes. Filtering it only at read time (getVisiblePickedIds) hid stale
    // ids without removing them, so they came back: sorting reloads page 1
    // without going through search(), and un-snoozing an asset re-adds a card
    // that was picked before it was snoozed — in both cases the row reappeared
    // already checked, from a selection the user believed was gone.
    if (this.props.items !== prevProps.items && this.state.pickedIds.length > 0) {
      const visible = this.getVisiblePickedIds();
      if (visible.length !== this.state.pickedIds.length) this.setState({ pickedIds: visible });
    }

    // Cache the All Assets total so its tab badge stays populated when the
    // attention scope is active (where items.total_count is the attention count).
    const machinesRequiringAttention = this.props.machinesRequiringAttention;
    const watchlistActive = (this.state.searchParams.watchlist || []).length > 0;
    const total = this.props.items && this.props.items.total_count;
    if (this.isAssetsPage && !machinesRequiringAttention
      && total != null && total !== this.state.allAssetsCount) {
      this.setState({ allAssetsCount: total });
    }
    // Cache the Needs Attention total (pure attention scope only) so the tab
    // badge doesn't pick up the watchlist-filtered count in Watchlist scope.
    if (this.isAssetsPage && machinesRequiringAttention && !watchlistActive
      && total != null && total !== this.state.attentionTabCount) {
      this.setState({ attentionTabCount: total });
    }

    // Handle AHS enabled state changes
    if (prevProps.ahsEnabled !== this.props.ahsEnabled) {
      const ahsEnabled = this.props.ahsEnabled !== false;
      this.setState((prevState) => {
        let selectedColumns = [...prevState.selectedColumns];

        if (!ahsEnabled) {
          // Remove health_score if AHS is disabled
          selectedColumns = selectedColumns.filter(col => col !== 'health_score');
        } else if (!selectedColumns.includes('health_score')) {
          // Add health_score if AHS is enabled and it's not already there
          selectedColumns = [...selectedColumns, 'health_score'];
        }

        return { selectedColumns };
      }, () => {
        this.updateAssetListLocalStorage('columns', this.state.selectedColumns);
      });
    }
  }

  componentWillUnmount() {
    // search() now touches state (it resets the selection), and the name search
    // is debounced by 500ms — without this, navigating away mid-debounce fires
    // setState on an unmounted component.
    this.nameSearchDebounce.cancel();
  }

  selectFilterFromCharts = (filter) => {
    let searchParams = {
      ...this.initialSearchParams
    };
    searchParams = {
      ...searchParams,
      [filter.name]: filter.value
    };
    this.setState({ searchParams }, this.updateUrlQueryString);
    this.search(searchParams);
  }

  getInitialSearchParams = () => {
    const { currentUser } = this.props;
    // Full-triage roles (analyst / petasense admin) start unfiltered; every other
    // role gets the stripped view defaulted to backend-visible actionable reasons.
    let attentionFilters = STRIPPED_ATTENTION_FILTERS;
    if (isFullTriageUser(currentUser)) {
      attentionFilters = [];
    }
    return {
      searchKey: '',
      state: [],
      condition: [],
      criticality: [],
      area: [],
      orientation: [],
      component_subtype: [],
      location: [],
      transmission: [],
      vfd: [],
      health_score: [],
      asset_setup: [],
      events: [],
      attention_criteria: attentionFilters,
      alarm_metrics: [],
      task_type: [],
      has_sensors: [],
      has_poor_wifi: [],
      has_unreviewed_alarms: [],
      watchlist: [],
      include_snoozed: false
    };
  }

  // Single rehydration path: URL -> scope + searchParams (every filter key,
  // incl. insight/chart filters). `isInitial` forces a fetch on mount even when
  // the URL only carries scope; on later (self-triggered) URL writes the
  // equality guard prevents a redundant refetch loop.
  selectFiltersFromUrl = (isInitial = false) => {
    const urlParams = new URLSearchParams(this.props.location.search);

    // Restore the scope tab (All Assets / Needs Attention) from the URL.
    const urlScope = urlParams.get('machines_requiring_attention');
    if (urlScope != null) {
      const show = urlScope === 'true';
      if (show !== this.props.machinesRequiringAttention) {
        this.props.machinesActions.setMachinesRequiringAttention(show, false);
      }
    }

    const searchParams = {
      ...this.initialSearchParams
    };
    searchParams.area = [...this.state.searchParams.area];
    Object.keys(searchParams).forEach((key) => {
      if (urlParams.get(key)) {
        searchParams[key] = isJsonString(urlParams.get(key)) ?
          JSON.parse(urlParams.get(key)) :
          urlParams.get(key);
      }
    });
    const sanitised = this.sanitiseParamsForRole(searchParams);
    const changed = !_.isEqual(this.state.searchParams, searchParams);
    // Rewrite the URL when a param was stripped, so the address bar stops
    // advertising a filter that is not applied (and a re-shared link is clean).
    // Terminates: the rewritten URL re-enters here, sanitises to a no-op, and
    // the equality guard below stops the refetch.
    const syncUrl = () => { if (sanitised && this.isAssetsPage) this.updateUrlQueryString(); };
    if (changed) this.setState({ searchParams }, syncUrl);
    else syncUrl();
    if (changed || isInitial) this.search(searchParams);
  }

  // Strip filters the current role is not permitted to use, mutating `params`
  // in place and reporting whether anything was removed.
  //
  // A URL is untrusted input here: it may have been shared by a colleague with
  // broader access, or hand-edited. The backend does not reject these — a
  // watchlist filter 403s outright, but a disallowed attention_criteria is
  // silently pruned to the role's allowed reasons and returns an empty 200. In
  // the criteria case the user is left worse off than with an error: the strip
  // renders no tab matching their filter (so not even "All" highlights), and
  // attention_criteria is deliberately strip-only rather than a removable chip,
  // so there is no control on screen that clears it.
  sanitiseParamsForRole = (params) => {
    const { currentUser } = this.props;
    let stripped = false;

    if (!canAccessWatchlist(currentUser) && (params.watchlist || []).length > 0) {
      params.watchlist = [];
      stripped = true;
    }

    if (!isFullTriageUser(currentUser)) {
      const allowed = (params.attention_criteria || [])
        .filter(key => STRIPPED_ATTENTION_FILTERS.includes(key));
      if (allowed.length !== (params.attention_criteria || []).length) {
        params.attention_criteria = allowed;
        stripped = true;
      }
    }

    return stripped;
  }

  clearAllFilters = () => {
    let searchParams = {
      ...this.initialSearchParams
    };

    if (!_.isEmpty(this.props.preFilters)) {
      searchParams = {
        ...searchParams,
        ...this.props.preFilters
      };
    }

    const fetchChartData = _.some(filtersIncludedInCharts, filter =>
      !_.isEqual(searchParams[filter], this.state.searchParams[filter])
    ) && this.props.showChartsView;

    this.setState({
      searchParams
    }, this.updateUrlQueryString);

    this.search(searchParams);
    if (fetchChartData) this.fetchChartData();
  }

  getNextMachines = () => {
    const {
      increaseMachinePaginater
    } = this.props.machinesActions;
    increaseMachinePaginater();
  };

  toggleFilterOpen(filter) {
    this.setState(prevState => ({
      filtersOpen: {
        ...prevState.filtersOpen,
        [filter]: !prevState.filtersOpen[filter]
      }
    }));
  }

  nameSearchDebounce = _.debounce(
    () => {
      this.search();
    }, 500);

  searchArea(key) {
    this.setState((prevState) => {
      const filteredAreas = _.filter(prevState.areaOptions, area => area.path.toLowerCase().includes(key.toLowerCase()));
      return ({
        areaSearchKey: key,
        areaDisplay: filteredAreas
      });
    });
  }


  search(params) {
    if (!params) {
      params = {
        ...this.state.searchParams
      };
    }
    const searchParams = {
      ...params,
    };
    this.props.machinesActions.setMachineSearchParams(searchParams);
    // This method is the choke point for applying filters, not just "searching":
    // setMachineSearchParams clears the list and refetches from page 1, so ids
    // picked against the previous result set are stale. Reset them here so a
    // selection cannot reappear when the new set happens to contain the same
    // ids. Sorting refetches WITHOUT passing through here — componentDidUpdate
    // covers that path by pruning against the new list.
    // The length check is load-bearing, not cosmetic: updateUrlQueryString
    // rewrites location.search, which re-enters selectFiltersFromUrl and calls
    // search() a second time on every filter change.
    if (this.state.pickedIds.length > 0) this.setState({ pickedIds: [] });
    // Keep the attention tab badge / criteria strip in sync with filters in any scope.
    if (this.isAssetsPage) this.fetchAttentionSummary();
    if (this.isAssetsPage && this.state.insightsOpen) this.fetchAttentionInsights();
  }

  changeFilter(filter, value) {
    this.setState((prevState) => {
      const searchParams = {
        ...prevState.searchParams,
        [filter]: value
      };
      return ({
        searchParams
      });
    }, () => {
      if (filter === 'searchKey') {
        this.nameSearchDebounce();
      } else {
        if (this.props.location.pathname === '/assets') this.updateUrlQueryString();
        this.search();
      }
    });
  }

  fetchChartData = () => {
    machineAnalyticsFilters.forEach(filter =>
      this.props.getMachinesAnalyticsData(filter)
    );
  }

  // Mirror the full filter state (every key + scope) to the URL so it's
  // shareable and survives reload. searchKey is intentionally left out.
  updateUrlQueryString = (scopeOverride) => {
    const scope = scopeOverride != null
      ? scopeOverride
      : this.props.machinesRequiringAttention;
    const params = { ...this.state.searchParams };
    if (scope) params.machines_requiring_attention = true;
    const queryString = generateQueryString(params, ['searchKey']);
    const pathname = this.props.location.pathname;
    history.replace({
      pathname,
      search: queryString
    });
  }

  selectMachine = (machine_id) => {
    this.props.hierarchyActions.selectNode(machine_id, 'machine');
    if (this.props.machinesRequiringAttention) {
      this.props.machinesActions.setAttentionMachineViewed(machine_id);
    }
  }

  // Triage card click opens the in-page detail pane (no navigation away,
  // and review stays explicit via the card/pane action).
  openAttentionDetail = (machine_id) => {
    this.setState({ selectedDetailMachineId: machine_id });
  }

  closeDetailPane = () => {
    this.setState({ selectedDetailMachineId: null });
  }

  toggleAssetReviewed = (machine_id, isReviewed) => {
    if (isReviewed) {
      this.props.machinesActions.unsetAttentionMachineViewed(machine_id);
    } else {
      this.props.machinesActions.setAttentionMachineViewed(machine_id);
    }
    this.fetchAttentionSummary();
  }

  fetchAttentionSummary = () => {
    if (this.isAssetsPage) {
      this.props.machinesActions.getAttentionSummary();
    }
  }

  selectAttentionCriteria = (key) => {
    this.changeFilter('attention_criteria', key === 'all' ? [] : [key]);
  }

  // Build removable chips for every applied filter (regardless of source:
  // + Filter popover, chart drill-down, etc.) — derived purely from searchParams.
  getAppliedFilterChips = () => {
    const { t } = this.props;
    return CHIP_FILTER_REGISTRY.reduce((chips, { key, titleKey, def }) => {
      const value = this.state.searchParams[key];
      if (value && value.length > 0) {
        chips.push({
          name: key,
          title: t(titleKey, { defaultValue: def }),
          text: this.getFilterText(key)
        });
      }
      return chips;
    }, []);
  }

  // The active metric insight id (has_*), if any — used for the toggle logic.
  getActiveMetricInsightId = () => {
    const sp = this.state.searchParams;
    const hitKey = Object.keys(INSIGHT_BY_KEY).find(k => (sp[k] || []).length > 0);
    return hitKey ? INSIGHT_BY_KEY[hitKey].filterId : null;
  }

  // Blue "insight" chips — derived from searchParams so every insight metric
  // filter in the URL is clearable. attention_criteria is intentionally NOT a
  // chip: it is "strip only", reflected solely by the active criteria tab.
  getActiveInsightChips = () => {
    const { t } = this.props;
    const sp = this.state.searchParams;
    const chips = [];
    const metricKey = Object.keys(INSIGHT_BY_KEY).find(k => (sp[k] || []).length > 0);
    if (metricKey) {
      const meta = INSIGHT_BY_KEY[metricKey];
      chips.push({ id: meta.filterId, label: t(meta.labelKey, { defaultValue: meta.def }) });
    }
    return chips;
  }

  // Drawer highlight: metric insight OR the single active criterion.
  getActiveInsightId = () => {
    const metricId = this.getActiveMetricInsightId();
    if (metricId) return metricId;
    const criteria = this.state.searchParams.attention_criteria || [];
    return criteria.length === 1 ? criteria[0] : null;
  }

  afterFilterChange = () => {
    if (this.props.location.pathname === '/assets') this.updateUrlQueryString();
    this.search();
    if (this.isAssetsPage) {
      this.fetchAttentionSummary();
      if (this.state.insightsOpen) this.fetchAttentionInsights();
    }
  }

  // A preset is active only when the applied chip-able filters match it exactly.
  getActiveAssetPreset = () => {
    const sortedCopy = arr => [...(arr || [])].sort();
    const matchesKey = (key, expected) =>
      _.isEqual(sortedCopy(this.state.searchParams[key]), sortedCopy(expected));
    const preset = ASSET_QUICK_PRESETS.find(p =>
      FILTER_CHIP_KEYS.every(key => matchesKey(key, p.patch[key] || [])));
    return preset ? preset.id : null;
  }

  toggleAssetPreset = (presetId) => {
    const preset = ASSET_QUICK_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    const isActive = this.getActiveAssetPreset() === presetId;
    this.setState((prevState) => {
      const searchParams = { ...prevState.searchParams };
      FILTER_CHIP_KEYS.forEach((key) => {
        searchParams[key] = isActive ? [] : (preset.patch[key] || []);
      });
      return { searchParams };
    }, this.afterFilterChange);
  }

  setFieldOptionSearch = (name, value) => {
    this.setState(prevState => ({
      fieldOptionSearch: { ...prevState.fieldOptionSearch, [name]: value }
    }));
  }

  // Option rows for a field inside the "+ Filter" popover, with an option
  // search box. Search is client-side by default; a field whose metadata
  // sets `backendSearch: true` + `onSearch` drives the options from the server.
  renderFieldOptions = (name) => {
    const { t } = this.props;
    const placeholder = t('assets.filterBar.searchOptions', { defaultValue: 'Search…' });

    // Area options are objects loaded up-front; the existing area search filters them.
    if (name === 'area') {
      return (
        <>
          <OptSearch>
            <Search />
            <OptSearchInput
              type="text"
              placeholder={placeholder}
              onChange={e => this.searchArea(e.target.value)}
              value={this.state.areaSearchKey}
            />
          </OptSearch>
          {this.renderFilterOptions(this.state.areaDisplay, 'area', 'name', false, 'path')}
        </>
      );
    }

    const meta = this.getFilterMetadata(name);
    const searchText = this.state.fieldOptionSearch[name] || '';
    const lc = searchText.toLowerCase();
    const options = meta.options || [];
    const filtered = (meta.backendSearch || !lc)
      ? options
      : options.filter((opt) => {
        if (opt && opt.groupName) return true;
        const label = String(getAssetListFilterOptionLabel(t, name, opt, meta.key) || '');
        return label.toLowerCase().includes(lc);
      });

    const handleSearch = (value) => {
      this.setFieldOptionSearch(name, value);
      if (meta.onSearch) meta.onSearch(value);
    };

    return (
      <>
        <OptSearch>
          <Search />
          <OptSearchInput
            type="text"
            placeholder={placeholder}
            value={searchText}
            onChange={e => handleSearch(e.target.value)}
          />
        </OptSearch>
        {this.renderFilterOptions(filtered, name, meta.key, meta.singleSelect)}
      </>
    );
  }

  getActiveSort = () => {
    const sorter = this.props.sorterState && this.props.sorterState.sorter;
    if (sorter && sorter.name && sorter.order) return `${sorter.name}|${sorter.order}`;
    return 'default';
  }

  changeAssetSort = (value) => {
    if (value === 'default') {
      this.props.machinesActions.setMachineSorter({});
      return;
    }
    const [name, order] = value.split('|');
    this.props.machinesActions.setMachineSorter({ name, order });
  }

  changeAttentionGroup = (value) => {
    this.setState({ attentionGroupBy: value });
  }

  // ---- Block 8: snooze / bulk / watchlist ----
  togglePick = (id) => {
    this.setState((prev) => {
      const pickedIds = prev.pickedIds.indexOf(id) !== -1
        ? prev.pickedIds.filter(x => x !== id)
        : [...prev.pickedIds, id];
      return { pickedIds };
    });
  }

  clearSelection = () => {
    this.setState({ pickedIds: [] });
  }

  // Rows can leave the list without the selection being rebuilt — snoozing an
  // asset drops its card in place via REMOVE_MACHINES, with no refetch. The
  // bulk bar and the bulk handlers read the selection through this intersection
  // so neither can reach an off-screen asset. componentDidUpdate prunes the
  // underlying state to match; this is the read-time safety net for the window
  // before that runs.
  getVisiblePickedIds = () => {
    const items = (this.props.items && this.props.items.object) || [];
    const visible = new Set(items.map(item => item.id));
    return this.state.pickedIds.filter(id => visible.has(id));
  }

  bulkReview = () => {
    const pickedIds = this.getVisiblePickedIds();
    if (!pickedIds.length) return;
    this.props.machinesActions.bulkMarkReviewed(pickedIds);
    this.setState({ pickedIds: [] });
  }

  bulkWatch = () => {
    const pickedIds = this.getVisiblePickedIds();
    if (!pickedIds.length) return;
    this.props.machinesActions.bulkAddToWatchlist(pickedIds);
    this.setState({ pickedIds: [] });
  }

  bulkSnooze = () => {
    const pickedIds = this.getVisiblePickedIds();
    if (!pickedIds.length) return;
    this.props.machinesActions.bulkSnoozeMachines(pickedIds, 7);
    this.setState({ pickedIds: [] });
  }

  snoozeAsset = (id, days) => {
    this.props.machinesActions.snoozeMachine(id, days);
    if (this.state.selectedDetailMachineId === id) this.closeDetailPane();
  }

  unsnoozeAsset = (id) => {
    this.props.machinesActions.unsnoozeMachine(id);
  }

  toggleAssetWatched = (id, watched) => {
    this.props.machinesActions.setMachineWatched(id, watched);
  }

  openAssetPage = (id) => {
    history.push(`/machines/${id}/overview`);
  }

  toggleSnoozed = () => {
    this.changeFilter('include_snoozed', !this.state.searchParams.include_snoozed);
  }

  fetchAttentionInsights = () => {
    const win = TREND_WINDOWS.find(w => w.id === this.state.insightTrendWindowId);
    this.props.machinesActions.getAttentionInsights(
      win ? { unit: win.unit, value: win.value } : null,
      Boolean(this.props.machinesRequiringAttention)
    );
  }

  toggleInsights = () => {
    this.setState((prev) => {
      const insightsOpen = !prev.insightsOpen;
      return { insightsOpen };
    }, () => {
      if (this.state.insightsOpen) this.fetchAttentionInsights();
    });
  }

  changeInsightTrendWindow = (id) => {
    this.setState({ insightTrendWindowId: id }, this.fetchAttentionInsights);
  }

  reorderInsights = (newOrder) => {
    this.setState((prev) => {
      this.saveInsightsLayout(newOrder, prev.insightsHidden);
      return { insightsOrder: newOrder };
    });
  }

  toggleInsightHidden = (id) => {
    this.setState((prev) => {
      const hidden = prev.insightsHidden.includes(id)
        ? prev.insightsHidden.filter(x => x !== id)
        : [...prev.insightsHidden, id];
      this.saveInsightsLayout(prev.insightsOrder, hidden);
      return { insightsHidden: hidden };
    });
  }

  // Apply an insight as a queue filter — all through searchParams (no separate
  // state). Metric insights show a blue chip; criteria insights drive the strip.
  applyInsight = (filterId) => {
    if (METRIC_INSIGHTS[filterId]) {
      const isActive = this.getActiveMetricInsightId() === filterId;
      this.setState((prev) => {
        const searchParams = { ...prev.searchParams };
        INSIGHT_METRIC_KEYS.forEach((k) => { searchParams[k] = []; });
        if (!isActive) {
          const m = METRIC_INSIGHTS[filterId];
          searchParams[m.key] = [{ value: m.value }];
        }
        return { searchParams };
      }, this.afterFilterChange);
      return;
    }
    // criteria-based insight row (e.g. baselines_status, missing_asset_info)
    const current = this.state.searchParams.attention_criteria || [];
    const nextKey = (current.length === 1 && current[0] === filterId) ? 'all' : filterId;
    const switchToAttention = !this.props.machinesRequiringAttention;
    if (switchToAttention) {
      this.props.machinesActions.setMachinesRequiringAttention(true, false);
    }
    this.setState((prev) => {
      const searchParams = { ...prev.searchParams };
      INSIGHT_METRIC_KEYS.forEach((k) => { searchParams[k] = []; });
      searchParams.attention_criteria = nextKey === 'all' ? [] : [nextKey];
      return { searchParams };
    }, () => {
      if (switchToAttention) this.updateUrlQueryString(true);
      this.search();
      this.fetchAttentionSummary();
    });
  }

  // Clear a single insight chip. `criteria:*` ids clear the criterion (strip);
  // any other id clears the metric insight keys.
  clearInsight = () => {
    this.setState((prev) => {
      const searchParams = { ...prev.searchParams };
      INSIGHT_METRIC_KEYS.forEach((k) => { searchParams[k] = []; });
      return { searchParams };
    }, this.afterFilterChange);
  }

  // Expand a compact insight widget into its full dynamic chart (reuses the
  // existing widget-data Widget — the same chart the old right panel used).
  openInsightDetail = (widgetKey, title) => {
    this.setState({ insightDetail: { key: widgetKey, title } });
  }

  closeInsightDetail = () => {
    this.setState({ insightDetail: null });
  }

  toggleResource = (value) => {
    const { selected } = this.state;
    const index = selected.indexOf(value);
    if (index === -1) selected.push(value);
    else selected.splice(index, 1);
    this.setState({
      selected
    });
    this.props.toggleModelResource(selected);
  }

  toggleSelectAll = () => {
    const { items } = this.props;
    const selectAll = items.object && items.object.length === this.state.selected.length;
    const selectSome = this.state.selected.length > 0;
    const ids = [];
    if (!(selectAll || selectSome)) {
      items.object.forEach((option) => {
        ids.push(option.id);
      });
    }
    this.setState({
      selected: ids
    });
    this.props.toggleModelResource(ids);
  };

  renderFilterOptions = (options, name, key = '', singleSelect = false, labelKey = '') => {
    if (_.isEmpty(options)) return null;
    const { t } = this.props;
    return options.map((option, index) => {
      const selected = this.state.searchParams[name] && this.state.searchParams[name].find(opt => _.isEqual(opt, option));
      const optionKey = labelKey || key;
      const isGroupHeader = Boolean(option.groupName);
      return (
        <FilterItem
          key={index.toString()}
          className={`dropdown-item ${isGroupHeader ? 'group-name' : ''}`}
          selected={selected}
          data-testid={isGroupHeader ? undefined : 'asset-filter-option-row'}
          data-filter-name={isGroupHeader ? undefined : name}
          data-group={isGroupHeader ? 'true' : undefined}
          onClick={() => {
            if (isGroupHeader) return;
            let options = [];
            if (selected) {
              options = _.filter(
                this.state.searchParams[name],
                opt => key ? opt[key] !== option[key] : opt !== option
              );
            } else if (singleSelect) {
              options = [option];
            } else {
              options = [...this.state.searchParams[name], option];
            }
            this.changeFilter(name, options);
          }}
        >
          {isGroupHeader ? (
            <OptLabel>{getAssetListFilterGroupLabel(t, option.groupName)}</OptLabel>
          ) : (
            <>
              {!singleSelect && (<OptCheckbox checked={Boolean(selected)} />)}
              <OptLabel>
                {getAssetListFilterOptionLabel(t, name, option, optionKey)}
              </OptLabel>
            </>
          )}
        </FilterItem>
      );
    });
  };

  getFilterText = (filter) => {
    const { t } = this.props;
    const selected = this.state.searchParams[filter];
    if (_.isEmpty(selected)) return '';

    const labelKeyForFilter = () => {
      if (filter === 'area') return 'path';
      const meta = this.getFilterMetadata(filter);
      if (meta && Object.prototype.hasOwnProperty.call(meta, 'key')) return meta.key;
      return '';
    };

    const labels = selected.map(item =>
      getAssetListFilterOptionLabel(t, filter, item, labelKeyForFilter())
    );

    if (labels.length === 1) return labels[0];
    if (labels.length >= 2) return `${labels[0]}, ${labels[1]}...`;
    return '';
  }

  setFilterOptions = (filter, options) => {
    this.setState(prevState => ({
      customFilterOptions: {
        ...prevState.customFilterOptions,
        [filter]: options
      }
    }));
  }

  getFilterMetadata = (filter) => {
    const { t } = this.props;
    switch (filter) {
      case 'state':
        return {
          title: t('assets.filters.state'),
          options: this.stateOptions,
          key: ''
        };
      case 'condition':
        return {
          title: t('assets.filters.condition'),
          options: getMachineConditionLabels(this.props.assetConditionLevels, t),
          key: ''
        };
      case 'criticality':
        return {
          title: t('assets.filters.criticality'),
          options: this.criticalityOptions,
          key: ''
        };
      case 'area':
        return {
          title: t('assets.filters.areas'),
          options: this.state.areaDisplay,
          key: 'name'
        };
      case 'orientation':
        return {
          title: t('assets.filters.orientation'),
          options: this.orientationOptions,
          key: ''
        };
      case 'component_subtype':
        return {
          title: t('assets.filters.componentTypes'),
          options: this.componentTypes,
          key: ''
        };
      case 'location':
        return {
          title: t('assets.filters.locationTypes'),
          options: this.state.locationTypes,
          key: 'text'
        };
      case 'transmission':
        return {
          title: t('assets.filters.transmission'),
          options: this.state.transmissionOptions,
          key: ''
        };
      case 'vfd':
        return {
          title: t('assets.filters.vfd'),
          options: this.getMachinesAnalyticsMetadata()[filter].filterOptions,
          key: 'text',
          singleSelect: true
        };
      case 'events':
        return {
          title: t('assets.filters.events'),
          options: this.state.customFilterOptions &&
            this.state.customFilterOptions.events,
          key: 'text',
          singleSelect: true
        };
      case 'health_score':
        return {
          title: t('assets.filters.healthScore'),
          options: this.getMachinesAnalyticsMetadata()[filter].filterOptions[
            this.props.assetConditionLevels
          ],
          key: 'text'
        };
      case 'asset_setup':
        return {
          title: t('assets.filters.assetSetup'),
          options: this.state.customFilterOptions &&
            this.state.customFilterOptions.asset_setup,
          key: 'text',
          singleSelect: true
        };
      case 'attention_criteria':
        return {
          title: t('assets.filters.attentionCriteria'),
          options: this.state.reasonsForAttention,
          key: ''
        };
      case 'alarm_metrics':
        return {
          title: t('assets.filters.alarmMetrics'),
          options: this.getAlarmMetricsMetadata().filterOptions,
          key: '',
          singleSelect: true
        };
      case 'task_type':
        return {
          title: t('assets.filters.taskType', { defaultValue: 'Task type' }),
          options: this.taskTypeOptions,
          key: ''
        };
      default:
        return null;
    }
  }

  getChartFilters = () => {
    const filters = getSearchParamFilters(this.state.searchParams, true);
    // true instead of !!machinesRequiringAttention due to backend checking for truthy values, not false
    if (this.props.machinesRequiringAttention) {
      filters.push({ name: 'machines_requiring_attention', op: 'eq', value: true });
    }
    return filters;
  };

  renderWidget = (chartFilters, metadata, width = '310px', height = '220px') => (
    <Widget
      setFilterOptions={this.setFilterOptions}
      filters={chartFilters}
      metadata={metadata}
      width={width}
      height={height}
    />
  );

  render() {
    const { t } = this.props;
    const {
      items,
      sorterState,
      showMoreOptions,
      ItemComponent,
      selectedId,
      border,
      borderRadius,
      margin,
      showFilterBorderBottom,
      listProps,
      showChartsView,
      listContainerBorder,
      listContainerBorderRadius,
      machinesRequiringAttention,
      headers: defaultHeaders,
      columnSizes: defaultColumnSizes,
      widgetsMetadata
    } = this.props;
    const {
      setMachineSorter,
      setMachinesRequiringAttention
    } = this.props.machinesActions;

    // Analysts + Petasense admins get the full analytical triage view (urgency
    // score, all reasons, insights) and watchlist. Every other role gets a
    // stripped "my work" view: configuration/action reasons only, no score.
    const fullTriageView = isFullTriageUser(this.props.currentUser);
    // Narrower than fullTriageView on purpose: the backend's
    // can_access_watchlist() excludes Petasense viewers, so offering them the
    // tab / star / bulk-watch would only produce a rejected request.
    const canUseWatchlist = canAccessWatchlist(this.props.currentUser);
    const strippedView = !fullTriageView;
    const watchlistActive = (this.state.searchParams.watchlist || []).length > 0;
    let attentionGroupOptions = null;
    if (machinesRequiringAttention) {
      attentionGroupOptions = strippedView
        ? ATTENTION_GROUP_OPTIONS.filter(o => o.value !== 'urgency')
        : ATTENTION_GROUP_OPTIONS;
    }
    let assetsScope = 'all';
    if (machinesRequiringAttention) assetsScope = watchlistActive ? 'watchlist' : 'attention';
    const summaryData = (this.props.attentionSummary && this.props.attentionSummary.data) || null;
    const currentTotal = items && items.total_count;
    const allAssetsCount = machinesRequiringAttention
      ? this.state.allAssetsCount
      : currentTotal;
    // Needs Attention badge = the full attention count, independent of the
    // watchlist filter. In Watchlist scope the live list/summary are
    // watchlist-filtered, so use the cached attention-scope count there.
    let attentionCount = null;
    if (machinesRequiringAttention && !watchlistActive) attentionCount = currentTotal;
    else if (watchlistActive) attentionCount = this.state.attentionTabCount;
    else if (summaryData) attentionCount = summaryData.total_count;
    const watchedCount = summaryData && summaryData.filter_facets && summaryData.filter_facets.watchlist
      ? summaryData.filter_facets.watchlist.watched : null;
    const assetsScopes = [
      {
        id: 'all', labelKey: 'assets.scope.allAssets', defaultLabel: 'All Assets', count: allAssetsCount
      },
      {
        id: 'attention',
        labelKey: strippedView ? 'assets.scope.myWork' : 'assets.scope.needsAttention',
        defaultLabel: strippedView ? 'My Work' : 'Needs Attention',
        count: attentionCount,
        alert: true
      }
    ];
    if (canUseWatchlist) {
      assetsScopes.push({
        id: 'watchlist', labelKey: 'assets.scope.watchlist', defaultLabel: 'Watchlist', count: watchedCount
      });
    }
    const selectAssetsScope = (nextScope) => {
      const scopeChanged = nextScope !== assetsScope;
      const showAttention = nextScope === 'attention' || nextScope === 'watchlist';
      const watchlistVal = nextScope === 'watchlist' ? [{ value: true }] : [];
      if (showAttention !== machinesRequiringAttention) {
        setMachinesRequiringAttention(showAttention, false);
      }
      this.setState(prev => ({
        searchParams: { ...prev.searchParams, watchlist: watchlistVal },
        insightsOpen: scopeChanged ? false : prev.insightsOpen
      }), () => {
        this.updateUrlQueryString(showAttention);
        this.search();
        if (showAttention) this.fetchAttentionSummary();
      });
    };

    const translateHeader = (header) => {
      if (header.label !== undefined && !header.labelKey) {
        return { ...header, label: header.label };
      }
      return {
        ...header,
        label: t(header.labelKey || `assets.headers.${header.name}`, {
          defaultValue: header.defaultValue != null ? header.defaultValue : header.label
        })
      };
    };

    let headers;
    let normalizedColumnSizes;
    let selectedColumnsForList;

    if (!showMoreOptions && defaultHeaders && defaultHeaders.length > 0) {
      headers = defaultHeaders.map(translateHeader);
      const totalSize = defaultColumnSizes.reduce((sum, size) => sum + size, 0);
      normalizedColumnSizes = totalSize > 0 ? defaultColumnSizes.map(size => (size / totalSize) * 100) : defaultColumnSizes;
      selectedColumnsForList = defaultHeaders.map(h => h.name);
    } else {
      const selectedColumns = this.state.selectedColumns;
      selectedColumnsForList = selectedColumns;

      // Ensure headers and columnSizes stay aligned
      const validSelectedColumns = selectedColumns.filter(col =>
        defaultHeaders.some(header => header.name === col)
      );

      headers = validSelectedColumns
        .map(col => defaultHeaders.find(header => header.name === col))
        .filter(Boolean)
        .map(translateHeader);

      const columnSizes = validSelectedColumns.map((col) => {
        const headerIndex = defaultHeaders.findIndex(h => h.name === col);
        return headerIndex >= 0 ? defaultColumnSizes[headerIndex] : 0;
      });

      // Normalize column sizes to sum to 100
      const totalSize = columnSizes.reduce((sum, size) => sum + size, 0);
      normalizedColumnSizes = totalSize > 0 ? columnSizes.map(size => (size / totalSize) * 100) : columnSizes;
    }

    const chartFilters = this.getChartFilters();

    return (
      <MachinesContainer id="machines-container" borderRadius={borderRadius} border={border} margin={margin} bg={this.isAssetsPage ? '#f6f7f4' : undefined}>
        {showMoreOptions && (
          <MoreOptions>
            <AccountMenuItems />
          </MoreOptions>
        )}
        <SearchFilters
          showBorderBottom={showFilterBorderBottom}
          style={this.isAssetsPage ? { padding: '0 20px' } : undefined}
        >
          <FlexContainer
            className="filter-misc-items"
            alignItems={this.isAssetsPage ? 'stretch' : 'center'}
            style={{ width: '100%' }}
          >
            {this.isAssetsPage && (
              <AssetsScopeTabs
                scope={assetsScope}
                scopes={assetsScopes}
                onSelectScope={selectAssetsScope}
              />
            )}
            {items && !this.isAssetsPage && (
              <FilterCountContainer>{items.total_count}</FilterCountContainer>
            )}
            <FlexContainer alignItems="center" style={{ marginLeft: 'auto', gap: '8px' }}>
              {this.isAssetsPage && !machinesRequiringAttention && (
                <EditDropdown
                  allColumns={this.allAssetColumns.filter(col => this.props.ahsEnabled !== false || col !== 'health_score')}
                  selectedColumns={this.state.selectedColumns}
                  toggleColumn={column => this.setState(prevState => (
                    prevState.selectedColumns.includes(column) ? ({
                      selectedColumns: prevState.selectedColumns.filter(item => item !== column)
                    }) : ({
                      // done this way to maintain order
                      selectedColumns: this.allAssetColumns.filter(item => (
                        prevState.selectedColumns.includes(item) ||
                        item === column
                      )).filter(col => this.props.ahsEnabled !== false || col !== 'health_score')
                    })
                  ), () => this.updateAssetListLocalStorage('columns', this.state.selectedColumns))}
                  title={t('assets.selectColumns')}
                  labelKeyPrefix="assets.headers"
                  renderTrigger={open => (
                    <GhostBtn type="button" onClick={open} title={t('assets.editColumns')}>
                      <ColumnIcon />
                      {t('assets.columns', { defaultValue: 'Columns' })}
                    </GhostBtn>
                  )}
                />
              )}
              {this.isAssetsPage && !machinesRequiringAttention && <MachineCsvToolbar />}
              <ToolbarSearchBox
                name="assets-search-box"
                placeholder={t('assets.searchPlaceholder')}
                onChange={e => this.changeFilter('searchKey', e.target.value)}
                value={this.state.searchParams.searchKey}
                clearLabel={t('common.clear')}
              />
              {this.props.allowAssetCreation && (
                <RBAC
                  resource={mapComponentToResource.Machines}
                  operation={operations.Create}
                  yes={(
                    <PrimaryLinkBtn title={t('assets.createNewAsset')} to="/machines/new">
                      <Plus />
                      {t('assets.addAsset', { defaultValue: 'Add Asset' })}
                    </PrimaryLinkBtn>
                  )}
                />
              )}
            </FlexContainer>
          </FlexContainer>
        </SearchFilters>
        {this.isAssetsPage && (
          <AppliedFilterBar
            chips={this.getAppliedFilterChips()}
            onRemoveChip={name => this.changeFilter(name, [])}
            onClearAll={this.clearAllFilters}
            presets={ASSET_QUICK_PRESETS}
            activePresetId={this.getActiveAssetPreset()}
            onTogglePreset={this.toggleAssetPreset}
            filterFields={(this.isAssetsPage
              ? [...this.props.filters, 'task_type']
              : this.props.filters
            ).map((name) => {
              const meta = this.getFilterMetadata(name);
              return meta ? { name, title: meta.title } : null;
            }).filter(Boolean)}
            renderFieldOptions={this.renderFieldOptions}
            sortOptions={machinesRequiringAttention ? ATTENTION_SORT_OPTIONS : ASSET_SORT_OPTIONS}
            activeSort={this.getActiveSort()}
            onChangeSort={this.changeAssetSort}
            groupOptions={attentionGroupOptions}
            activeGroup={this.state.attentionGroupBy}
            onChangeGroup={this.changeAttentionGroup}
            showInsights={!strippedView}
            insightsOpen={this.state.insightsOpen}
            onToggleInsights={this.toggleInsights}
            insightChips={this.getActiveInsightChips()}
            onClearInsight={this.clearInsight}
          />
        )}
        {this.isAssetsPage && !strippedView && this.state.insightsOpen && (() => {
          const insights = (this.props.attentionInsights && this.props.attentionInsights.data) || null;
          const insightsLoading = this.props.attentionInsights && this.props.attentionInsights.loading;
          return (
            <InsightsDrawer
              data={insights}
              loading={Boolean(insightsLoading) && !insights}
              order={this.state.insightsOrder}
              hidden={this.state.insightsHidden}
              activeInsightId={this.getActiveInsightId()}
              trendWindowId={this.state.insightTrendWindowId}
              onApplyInsight={this.applyInsight}
              onReorder={this.reorderInsights}
              onToggleHidden={this.toggleInsightHidden}
              onChangeTrendWindow={this.changeInsightTrendWindow}
              onExpand={this.openInsightDetail}
            />
          );
        })()}
        {this.isAssetsPage && machinesRequiringAttention && (() => {
          const summary = (this.props.attentionSummary && this.props.attentionSummary.data) || {};
          const attentionCriteria = this.state.searchParams.attention_criteria || [];
          return (
            <CriteriaStrip
              byCriteria={summary.by_criteria}
              triageProgress={summary.triage_progress}
              totalCount={summary.total_count}
              selectedCriteria={attentionCriteria.length === 1 ? attentionCriteria[0] : 'all'}
              onSelectCriteria={this.selectAttentionCriteria}
              snoozedCount={summary.snoozed_count || 0}
              showSnoozed={Boolean(this.state.searchParams.include_snoozed)}
              onToggleSnoozed={this.toggleSnoozed}
              stripped={strippedView}
            />
          );
        })()}
        <FlexContainer
          direction={this.isAssetsPage && !machinesRequiringAttention ? 'column' : undefined}
          innerRef={(node) => { this.attentionScrollRef.current = node; }}
          style={{
            overflow: this.isAssetsPage ? 'hidden' : 'auto',
            ...(this.isAssetsPage ? { height: this.props.height, minHeight: 0 } : {})
          }}
        >
          {this.isAssetsPage && machinesRequiringAttention && (
            <FlexContainer style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              height: '100%',
              alignItems: 'stretch'
            }}
            >
              <AttentionQueue
                items={items}
                groupBy={this.state.attentionGroupBy}
                onSelect={this.openAttentionDetail}
                onToggleReviewed={this.toggleAssetReviewed}
                selectedId={this.state.selectedDetailMachineId}
                loading={items.loading}
                hasMore={items.hasMore}
                loadMoreItems={this.getNextMachines}
                emptyMsg={t('assets.noResults')}
                canWatchlist={canUseWatchlist}
                pickedIds={this.getVisiblePickedIds()}
                onTogglePick={this.togglePick}
                onClearSelection={this.clearSelection}
                onBulkReview={this.bulkReview}
                onBulkSnooze={this.bulkSnooze}
                onBulkWatch={this.bulkWatch}
                onSnooze={this.snoozeAsset}
                onUnsnooze={this.unsnoozeAsset}
                onToggleWatch={this.toggleAssetWatched}
                onOpenAsset={this.openAssetPage}
                stripped={strippedView}
              />
              {this.state.selectedDetailMachineId && (
                <AttentionDetailPane
                  item={(items.object || []).find(m => m.id === this.state.selectedDetailMachineId) || null}
                  onClose={this.closeDetailPane}
                  onToggleReviewed={this.toggleAssetReviewed}
                  stripped={strippedView}
                  scrollContainerRef={this.attentionScrollRef}
                />
              )}
            </FlexContainer>
          )}
          {this.isAssetsPage && !machinesRequiringAttention && (
            <AssetsTable
              headers={headers}
              items={items}
              sorterState={sorterState}
              setSorter={setMachineSorter}
              onMachineSelect={this.selectMachine}
              conditionLevels={this.props.assetConditionLevels}
              loading={items.loading}
              hasMore={items.hasMore}
              loadMoreItems={this.getNextMachines}
              emptyMsg={t('assets.noResults')}
            />
          )}
          {!this.isAssetsPage && (
          <ListContainer border={listContainerBorder} borderRadius={listContainerBorderRadius}>
            <List
              height={this.props.height}
              columnSizes={normalizedColumnSizes}
              headers={headers}
              maxHeightAfterScroll={this.props.maxHeightAfterScroll}
              sorterState={sorterState}
              setSorter={setMachineSorter}
              items={items}
              hasMore={items.hasMore}
              loading={items.loading}
              loadMoreItems={this.getNextMachines}
              ItemComponent={ItemComponent}
              onMachineSelect={this.selectMachine}
              emptyMsg={t('assets.noResults')}
              padding={this.props.padding}
              allSelected={items.object && items.object.length === this.state.selected.length}
              selectAll={this.toggleSelectAll}
              toggleResource={id => this.toggleResource(id)}
              selectedId={selectedId}
              selectedOptions={this.state.selected}
              editMode={this.props.editMode}
              border={this.props.border}
              selectedColumns={selectedColumnsForList}
              {...listProps}
            />
          </ListContainer>
          )}
          {/* On the Assets page the Insights drawer replaces this right-side
              widgets panel; keep it for any embedded/non-assets usage. */}
          {showChartsView && !this.isAssetsPage && (
            <CollapsibleWidgetsContainer componentName="Machines">
              {!widgetsMetadata.loading && !_.isEmpty(widgetsMetadata.object) && (
                <>
                  {this.renderWidget(chartFilters, widgetsMetadata.object.incomplete_asset_setup_breakdown)}
                  {this.renderWidget(chartFilters, widgetsMetadata.object.open_events_breakdown)}
                  {this.renderWidget(chartFilters, widgetsMetadata.object.vfd_breakdown)}
                  {this.renderWidget(chartFilters, widgetsMetadata.object.asset_health_scores_breakdown)}
                </>
              )}
            </CollapsibleWidgetsContainer>
          )}
        </FlexContainer>
        {
          this.state.showMachineBuilderModal && (
            <MachineBuilderModal
              close={(id) => {
                this.setState({ showMachineBuilderModal: false });
                if (id) this.props.machinesActions.initMachinePaginater();
              }}
            />
          )
        }
        {this.state.insightDetail
          && widgetsMetadata.object
          && widgetsMetadata.object[this.state.insightDetail.key] && (
          <Modal
            title={this.state.insightDetail.title}
            width="44rem"
            padding="1rem"
            headerPadding="1rem"
            showHeaderBorder
            close={this.closeInsightDetail}
          >
            <FlexContainer justifyContent="center" padding="1rem">
              {this.renderWidget(
                chartFilters,
                widgetsMetadata.object[this.state.insightDetail.key],
                '600px',
                '340px'
              )}
            </FlexContainer>
          </Modal>
        )}
      </MachinesContainer>
    );
  }
}

MachinesPage.propTypes = {
  showMoreOptions: PropTypes.bool,
  columnSizes: PropTypes.arrayOf(PropTypes.number),
  headers: PropTypes.arrayOf(PropTypes.object),
  ItemComponent: PropTypes.func,
  filters: PropTypes.arrayOf(PropTypes.string),
  selectedId: PropTypes.number,
  selectedOptions: PropTypes.arrayOf(PropTypes.number),
  editMode: PropTypes.bool,
  border: PropTypes.string,
  preFilters: PropTypes.object,
  padding: PropTypes.string,
  height: PropTypes.string,
  maxHeightAfterScroll: PropTypes.string,
  allowAssetCreation: PropTypes.bool,
  showFilterBorderBottom: PropTypes.bool,
  listProps: PropTypes.object,
  showChartsView: PropTypes.bool,
  listContainerBorder: PropTypes.string,
  listContainerBorderRadius: PropTypes.string,
};

MachinesPage.defaultProps = {
  showMoreOptions: true,
  columnSizes: [20, 12, 12, 10, 12, 10, 12, 12],
  headers: [{
    name: 'name',
    labelKey: 'assets.headers.machine',
    defaultValue: 'Machine'
  }, {
    name: 'condition',
    labelKey: 'assets.headers.condition',
    defaultValue: 'Condition'
  }, {
    name: 'area_name',
    labelKey: 'assets.headers.area',
    defaultValue: 'Area'
  }, {
    name: 'current_state',
    labelKey: 'assets.headers.state',
    defaultValue: 'State',
    alignCenter: true
  }, {
    name: 'last_measurement_time',
    labelKey: 'assets.headers.lastMeasurement',
    defaultValue: 'Last Measurement',
    alignCenter: true
  }, {
    name: 'setup_completion_percent',
    labelKey: 'assets.headers.setup_completion_percent',
    defaultValue: 'Setup completion %',
    alignCenter: true
  }, {
    name: 'condition_since',
    labelKey: 'assets.headers.condition_since',
    defaultValue: 'Condition Since',
    alignCenter: true
  }, {
    name: 'health_score',
    labelKey: 'assets.headers.healthScore',
    defaultValue: 'Health Score',
    alignCenter: true
  }],
  ItemComponent: MachineItem,
  filters: [
    'area',
    'condition',
    'criticality',
    'state',
    'orientation',
    'component_subtype',
  ],
  selectedId: null,
  selectedOptions: null,
  editMode: null,
  border: null,
  preFilters: {},
  padding: '0 1em',
  height: 'calc(100vh - var(--title-bar-offset, 0px) - 226px)',
  maxHeightAfterScroll: 'calc(100vh - var(--title-bar-offset, 0px) - 226px)',
  allowAssetCreation: true,
  showFilterBorderBottom: true,
  listProps: null,
  showChartsView: true,
  listContainerBorder: '',
  listContainerBorderRadius: ''
};

const mapStateToProps = state => ({
  query: state.machines.query,
  items: state.machines.items,
  sorterState: state.machines.sorter,
  machinesRequiringAttention: state.machines.machinesRequiringAttention.show,
  attentionSummary: state.machines.attentionSummary,
  attentionInsights: state.machines.attentionInsights,
  currentUser: state.user.user,
  assetConditionLevels: state.currentAccount.preferences.machine_condition_levels,
  analyticsFilters: state.machinesAnalytics,
  widgetsMetadata: state.widgets.metadata,
  title: state.companyReducer.partner.title,
  ahsEnabled: (
    state.currentAccount.preferences.enabled_features &&
    state.currentAccount.preferences.enabled_features.ahs_enabled !== false
  ) || (
    !state.currentAccount.preferences.enabled_features
  )
});

const mapDispatchToProps = dispatch => ({
  machinesActions: bindActionCreators(machinesActions, dispatch),
  hierarchyActions: bindActionCreators(hierarchyActions, dispatch),
  machineBuilderActions: bindActionCreators(machineBuilderActions, dispatch),
  getMachinesAnalyticsData: bindActionCreators(getMachinesAnalyticsData, dispatch),
  widgetActions: bindActionCreators(widgetActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withRouter(withTranslation()(MachinesPage))
);
